import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'CUSTOMER' | 'ADMIN';
}

// Resposta do GET/PATCH /auth/me
export interface UserProfile extends AuthUser {
  // false nas contas criadas pelo Google que ainda não definiram senha
  hasPassword: boolean;
  // null enquanto o cliente não informou os dados de contato
  phone: string | null;
  address: string | null;
}

interface AuthResponse {
  access_token: string;
  user: AuthUser;
}

const TOKEN_KEY = 'jwt_token';
const USER_KEY = 'user_data';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/auth`;

  // Restaurado do localStorage para manter a sessão ao recarregar a página
  private readonly currentUser = signal<AuthUser | null>(this.readStoredUser());

  readonly user = this.currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly isAdmin = computed(() => this.currentUser()?.role === 'ADMIN');

  googleLogin(googleToken: string) {
    return this.authenticate('google-login', { token: googleToken });
  }

  // Login com email e senha
  login(credentials: { email: string; password: string }) {
    return this.authenticate('login', credentials);
  }

  // Cadastro com email e senha (já devolve a sessão iniciada)
  register(data: { name: string; email: string; password: string }) {
    return this.authenticate('register', data);
  }

  private authenticate(endpoint: string, body: object) {
    return this.http.post<AuthResponse>(`${this.apiUrl}/${endpoint}`, body)
      .pipe(tap(response => this.storeSession(response)));
  }

  private storeSession(response: AuthResponse) {
    localStorage.setItem(TOKEN_KEY, response.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    this.currentUser.set(response.user);
  }

  // Dados da própria conta (tela "Meus dados")
  getProfile() {
    return this.http.get<UserProfile>(`${this.apiUrl}/me`);
  }

  updateProfile(data: { name: string; phone?: string; address?: string }) {
    return this.http.patch<UserProfile>(`${this.apiUrl}/me`, data).pipe(
      tap(profile => {
        // Mantém o nome do cabeçalho e da sessão guardada em dia
        const user = this.currentUser();
        if (!user) return;
        const updated = { ...user, name: profile.name };
        localStorage.setItem(USER_KEY, JSON.stringify(updated));
        this.currentUser.set(updated);
      })
    );
  }

  changePassword(data: { currentPassword?: string; newPassword: string }) {
    // A troca invalida os tokens antigos: a API devolve um novo para esta sessão continuar
    return this.http.patch<AuthResponse>(`${this.apiUrl}/me/password`, data)
      .pipe(tap(response => this.storeSession(response)));
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getUserRole(): string {
    return this.currentUser()?.role ?? 'CUSTOMER';
  }

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.currentUser.set(null);
  }

  private readStoredUser(): AuthUser | null {
    if (!localStorage.getItem(TOKEN_KEY)) return null;
    try {
      const user = JSON.parse(localStorage.getItem(USER_KEY) ?? 'null');
      return user?.id ? user : null;
    } catch {
      return null;
    }
  }
}
